/// <amd-module name="TFS/Admin/OrganizationAadCommon/Sources/OrganizationAadSource" />
import { getOrganizationClient } from "TFS/Admin/Common/RestClient/Organization";
import { Organization } from "TFS/Admin/Common/Contracts/Organization";
import { JsonPatchDocument, JsonPatchOperation, Operation } from "VSS/Platform/Contracts/WebApi";
import { IVssComponentContext } from "VSS/Platform/Layout";
import { EmptyGuidString } from "VSS/Core/Util/String";
import { DataProviders } from "TFS/Admin/Common/Constants/DataProviderConstants";
import { IOrganizationSettingsRestClient } from "TFS/Admin/Common/RestClient/OrganizationSettings";
import { DisconnectedUsers } from "TFS/Admin/Common/Contracts/OrganizationSettings";
import { IVssPageContext } from "VSS/Platform/Context";
import { IGraphRestClient } from "TFS/Admin/Common/RestClient/Graph";
import { GraphUser, GraphUserOriginIdUpdateContext } from "VSS/Platform/Contracts/Graph";
import { IUserMappingData, IOrganizationTokenData } from "Contracts/DataModels";
import { IInvitationRestClient } from "TFS/Admin/Common/RestClient/Invitation";
import { InvitationData, InvitationType } from "TFS/Admin/Common/Contracts/Invitation";
import { Invitee } from "TFS/Admin/Common/Contracts/Invitation";
import { getEnterpriseCatalogClient } from "TFS/Admin/Common/RestClient/EnterpriseCatalog";
import { TeamFoundationHostType, IVssRestClientOptions } from "VSS/Platform/Context";
import { BearerTokenProvider } from "VSS/Platform/Authentication";
import { ITenantPolicyRestClient } from "TFS/Admin/Common/RestClient/TenantPolicy";
import { Policy } from "TFS/Admin/Common/Contracts/TenantPolicy";
import { IAadTenantPolicy } from "TFS/Admin/OrganizationAadCommon/Contracts/DataModels";
import { PolicyNames, PolicyProperties } from "TFS/Admin/Common/Constants/Generated/Constants";

export class OrganizationAadSource {
    public updateAadTenant(tenantId: string, context: IVssComponentContext): Promise<Organization> {
        tenantId = tenantId ? tenantId : EmptyGuidString;

        let createPatchDocument = (): JsonPatchDocument => {
            let operations: JsonPatchOperation[] = [];
            operations.push({
                from: "",
                op: Operation.Replace,
                path: DataProviders.TenantId,
                value: tenantId
            } as JsonPatchOperation);
            return operations as JsonPatchDocument;
        };

        let client = this.getCollectionClient(context);

        return client.updateOrganization(createPatchDocument(), DataProviders.Me);
    }

    public getDisconnectedUsers(tenantId: string, context: IVssPageContext): Promise<DisconnectedUsers> {
        let client = this.getOrganizationSettingsClient(context);
        return client.getDisconnectedUsers(tenantId);
    }

    public downloadConnectedOrgs(tenantId: string, context: IVssPageContext, authToken: IOrganizationTokenData | undefined) {
        let client = this.getEnterpriseCatelogClient(context, authToken);
        return client.getCollectionsByTenantId(tenantId);
    }

    public mapUnresolvedUsers(context: IVssPageContext, userMappingData: IUserMappingData): Promise<GraphUser> {
        let client = this.getGraphClient(context);
        let updateContext: GraphUserOriginIdUpdateContext = {
            originId: userMappingData.mappingOriginId,
            storageKey: ""
        };
        return client.updateUser(updateContext, userMappingData.currentUserDescriptor);
    }

    public sendInvitations(context: IVssPageContext, invitees: Invitee[]) {
        let client = this.getInvitationClient(context);
        const invitationData = {
            attributes: {},
            invitationType: InvitationType.DirectoryInvite,
            invitees: invitees
        } as InvitationData;
        return client.sendInvitations(invitationData);
    }

    public updateOrgCreationPolicy(context: IVssPageContext, updatedPolicyData: IAadTenantPolicy): Promise<void> {
        let client = this.getTenantPolicyClient(context);
        let allowedObjectIds: string[] = [];

        if (updatedPolicyData.properties && updatedPolicyData.properties[PolicyProperties.AllowedUsersAndGroupObjectIds]) {
            updatedPolicyData.properties[PolicyProperties.AllowedUsersAndGroupObjectIds].forEach((aadObject: { objectId: string; }) => {
                allowedObjectIds.push(aadObject.objectId);
            });
        }

        const policyData: Policy = {
            name: PolicyNames.OrganizationCreationRestriction,
            value: updatedPolicyData.isPolicyEnabled,
            properties: {
                [PolicyProperties.AllowedUsersAndGroupObjectIds]: JSON.stringify(allowedObjectIds),
                [PolicyProperties.ErrorMessage]: updatedPolicyData.properties ? updatedPolicyData.properties[PolicyProperties.ErrorMessage] : ""
            }
        };
        return client.setPolicy(policyData, updatedPolicyData.policyName);
    }

    public updateGlobalPatCreationPolicy(context: IVssPageContext, updatedPolicyData: IAadTenantPolicy): Promise<void> {
        let client = this.getTenantPolicyClient(context);
        let allowedObjectIds: string[] = [];
        if (updatedPolicyData.properties && updatedPolicyData.properties[PolicyProperties.AllowedUsersAndGroupObjectIds]) {
            updatedPolicyData.properties[PolicyProperties.AllowedUsersAndGroupObjectIds].forEach((aadObject: { objectId: string; }) => {
                allowedObjectIds.push(aadObject.objectId);
            });
        }

        const policyData: Policy = {
            name: PolicyNames.RestrictGlobalPersonalAccessToken,
            value: updatedPolicyData.isPolicyEnabled,
            properties: {
                [PolicyProperties.AllowedUsersAndGroupObjectIds]: JSON.stringify(allowedObjectIds)
            }
        };
        return client.setPolicy(policyData, PolicyNames.RestrictGlobalPersonalAccessToken);
    }

    public updateFullScopePatCreationPolicy(context: IVssPageContext, updatedPolicyData: IAadTenantPolicy): Promise<void> {
        // This policy doesn't currently have any data to update, so properties is empty
        let client = this.getTenantPolicyClient(context);
        let allowedObjectIds: string[] = [];
        if (updatedPolicyData.properties && updatedPolicyData.properties[PolicyProperties.AllowedUsersAndGroupObjectIds]) {
            updatedPolicyData.properties[PolicyProperties.AllowedUsersAndGroupObjectIds].forEach((aadObject: { objectId: string; }) => {
                allowedObjectIds.push(aadObject.objectId);
            });
        }

        const policyData: Policy = {
            name: PolicyNames.RestrictFullScopePersonalAccessToken,
            value: updatedPolicyData.isPolicyEnabled,
            properties: {
                [PolicyProperties.AllowedUsersAndGroupObjectIds]: JSON.stringify(allowedObjectIds)
            }
        };
        return client.setPolicy(policyData, PolicyNames.RestrictFullScopePersonalAccessToken);
    }

    public updateTokenLifespanPolicy(context: IVssPageContext, updatedPolicyData: IAadTenantPolicy): Promise<void> {
        let client = this.getTenantPolicyClient(context);
        let allowedObjectIds: string[] = [];
        if (updatedPolicyData.properties && updatedPolicyData.properties[PolicyProperties.AllowedUsersAndGroupObjectIds]) {
            updatedPolicyData.properties[PolicyProperties.AllowedUsersAndGroupObjectIds].forEach((aadObject: { objectId: string; }) => {
                allowedObjectIds.push(aadObject.objectId);
            });
        }

        const policyData: Policy = {
            name: PolicyNames.RestrictPersonalAccessTokenLifespan,
            value: updatedPolicyData.isPolicyEnabled,
            properties: {
                [PolicyProperties.MaxPatLifespanInDays]: updatedPolicyData.properties ? updatedPolicyData.properties[PolicyProperties.MaxPatLifespanInDays] : "30",
                [PolicyProperties.AllowedUsersAndGroupObjectIds]: JSON.stringify(allowedObjectIds)
            }
        };

        return client.setPolicy(policyData, PolicyNames.RestrictPersonalAccessTokenLifespan);
    }

    public updateTokenAutoRevocationPolicy(context: IVssPageContext, updatedPolicyData: IAadTenantPolicy): Promise<void> {
        let client = this.getTenantPolicyClient(context);        
        const policyData: Policy = {
            name: PolicyNames.EnableLeakedPersonalAccessTokenAutoRevocation,
            value: updatedPolicyData.isPolicyEnabled,
            properties: {}
        };

        return client.setPolicy(policyData, PolicyNames.EnableLeakedPersonalAccessTokenAutoRevocation);
    }

    private getCollectionClient(context: IVssComponentContext) {
        return getOrganizationClient(context.pageContext);
    }

    private getOrganizationSettingsClient(context: IVssPageContext) {
        return context.getRestClient<IOrganizationSettingsRestClient>("IOrganizationSettingsRestClient");
    }

    private getGraphClient(context: IVssPageContext) {
        return context.getRestClient<IGraphRestClient>("IGraphRestClient");
    }

    private getInvitationClient(context: IVssPageContext) {
        return context.getRestClient<IInvitationRestClient>("IInvitationRestClient");
    }

    private getEnterpriseCatelogClient(context: IVssPageContext, authToken: IOrganizationTokenData | undefined) {
        return getEnterpriseCatalogClient(context, {
            hostType: TeamFoundationHostType.Deployment,
            serviceInstanceType: OrganizationServiceInstanceTypes.ServiceInstanceTypeAex,
            authTokenProvider:
                authToken && authToken.deploymentLevelSessionToken ? new BearerTokenProvider(authToken.deploymentLevelSessionToken.token) : undefined
        } as IVssRestClientOptions);
    }

    private getTenantPolicyClient(context: IVssPageContext) {
        return context.getRestClient<ITenantPolicyRestClient>("ITenantPolicyRestClient");
    }
}

export namespace OrganizationServiceInstanceTypes {
    export const ServiceInstanceTypeAex = "00000041-0000-8888-8000-000000000000";
}
